/**
 * SPLICE統一APIレスポンス型定義
 * 
 * SPLICE全体エコシステム（メインシステム + 3D Analyzer）で
 * 一貫したAPIレスポンス形式の型定義
 */

// ===== 基本レスポンス型 =====

export interface ApiMeta {
  /** レスポンス生成時刻（ISO8601形式） */
  timestamp: string;
  /** システム識別子 */
  system: 'splice' | '3danalyzer';
  /** APIバージョン */
  version: string;
  /** ページネーション情報（該当する場合） */
  pagination?: PaginationMeta;
  /** イベント情報（Webhook等） */
  event?: string;
  /** 同期タイプ（システム間同期） */
  sync_type?: string;
  /** 認証タイプ */
  auth_type?: string;
}

export interface PaginationMeta {
  /** 現在のページ番号 */
  page: number;
  /** ページサイズ */
  page_size: number;
  /** 総件数 */
  total_count: number;
  /** 総ページ数 */
  total_pages: number;
  /** 次のページが存在するか */
  has_next: boolean;
  /** 前のページが存在するか */
  has_previous: boolean;
}

/**
 * 統一APIレスポンス基本形式
 */
export interface StandardApiResponse<T = unknown> {
  /** 操作ステータス */
  status: 'success' | 'error';
  /** 操作メッセージ（オプション） */
  message?: string;
  /** レスポンスデータ（成功時） */
  data?: T;
  /** エラー詳細（失敗時） */
  errors?: ValidationErrors | string | string[];
  /** メタデータ */
  meta: ApiMeta;
}

/**
 * バリデーションエラー形式
 */
export interface ValidationErrors {
  [field: string]: string[];
}

// ===== 特定用途のレスポンス型 =====

/**
 * ページネーション付きレスポンス
 */
export interface PaginatedApiResponse<T = unknown> extends StandardApiResponse<T[]> {
  meta: ApiMeta & {
    pagination: PaginationMeta;
  };
}

/**
 * 認証レスポンス
 */
export interface AuthApiResponse extends StandardApiResponse<{
  user: UserData;
  authentication: TokenData;
}> {
  meta: ApiMeta & {
    auth_type: string;
  };
}

/**
 * Webhookレスポンス
 */
export interface WebhookApiResponse<T = unknown> extends StandardApiResponse<T> {
  meta: ApiMeta & {
    event: string;
  };
}

/**
 * 同期レスポンス
 */
export interface SyncApiResponse<T = unknown> extends StandardApiResponse<T> {
  meta: ApiMeta & {
    sync_type: string;
  };
}

// ===== データ型定義 =====

/**
 * ユーザーデータ統一形式
 */
export interface UserData {
  id: number;
  username: string;
  email: string;
  profile: {
    first_name: string;
    last_name: string;
    organization: string;
    display_name?: string;
  };
  subscription: {
    plan: string;
    status: 'active' | 'inactive';
    start_date: string;
  };
  permissions: {
    is_staff: boolean;
    is_superuser: boolean;
    is_active: boolean;
  };
  timestamps: {
    date_joined: string;
    last_login: string | null;
  };
}

/**
 * 認証トークンデータ
 */
export interface TokenData {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

/**
 * サブスクリプションプラン統一形式
 */
export interface SubscriptionPlan {
  name: string;
  display_name: string;
  description: string;
  pricing: {
    monthly: number;
    currency: 'JPY';
    formatted: string;
  };
  limits: {
    storage_gb: number;
    projects: number | null; // null = unlimited
    data_per_project: number | null; // null = unlimited
  };
  features: {
    allowed_file_formats: string[];
    api_access: boolean;
  };
  meta: {
    is_active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
  };
}

/**
 * 統計データ
 */
export interface StatsData {
  overview: {
    total_users: number;
    active_users: number;
    total_projects: number;
    storage_usage_gb: number;
  };
  service_breakdown: {
    [service: string]: {
      users: number;
      usage: number;
    };
  };
}

// ===== エラー型定義 =====

/**
 * API エラー詳細
 */
export interface ApiError {
  status: 'error';
  message: string;
  errors?: ValidationErrors | string | string[];
  meta: ApiMeta;
}

/**
 * ネットワークエラー
 */
export interface NetworkError {
  type: 'network';
  message: string;
  status?: number;
  url?: string;
}

/**
 * 全体エラー型
 */
export type ApiResponseError = ApiError | NetworkError;



// ===== ユーティリティ型 =====

/**
 * APIレスポンスの判定用型ガード
 */
export const isStandardApiResponse = (response: unknown): response is StandardApiResponse => {
  return !!(response && 
         typeof response === 'object' && 
         'status' in response && 
         'meta' in response &&
         ((response as { status: string }).status === 'success' || (response as { status: string }).status === 'error'));
};



export const isApiError = (response: unknown): response is ApiError => {
  return isStandardApiResponse(response) && response.status === 'error';
};

// ===== APIバージョン管理 =====

export type ApiVersion = 'v1' | 'v2';

export interface ApiRequestConfig {
  version?: ApiVersion;
  timeout?: number;
  retries?: number;
}